import React from 'react';
import { flushSync } from 'react-dom';
import { idGenerator } from '~/lib/helper/IdHelper';

import './Combobox.scss';

const comboboxIdGen = idGenerator('combobox-');

/** A suggestion for a {@link FreeTextCombobox}: `value` is what the field takes, `label` what the list shows. */
export type ComboboxItem = {
    value: string;
    label: string;
};

type CommonProps = {
    value: string;
    onChange: (value: string) => void;
    /** Applied to the wrapper, which is the flex item when a `SelectCombobox` is placed in an `InputGroup`. */
    className?: string;
    /**
     * Maps an item's label and value, and the trimmed typed text, to a comparable form. Filtering keeps
     * the items whose normalised label or value contains the normalised text, highlighting one whose
     * normalised label or value equals it. Defaults to lower-casing.
     */
    normalize?: (text: string) => string;
    /**
     * Shown in place of the list when nothing matches the typed text. A free-text field with no
     * suggestions at all shows "No suggestions" instead.
     */
    emptyText: string;
};

type SelectComboboxProps = CommonProps & {
    /** The choices, unique and in display order. Only these can be picked; typed text is never committed. */
    items: readonly string[];
    'aria-label': string;
    /** Shown above the list while no text is typed, and as the input's description. */
    hint?: string;
};

type FreeTextComboboxProps = CommonProps & {
    /** Suggestions, with unique `value`s, in display order. The value is whatever is typed; picking one sets its `value`. */
    items: readonly ComboboxItem[];
    invalid?: boolean;
    placeholder?: string;
} & /** Named by a `<Label for={id}>`, or by `aria-label`. */ (
        | { id: string; 'aria-label'?: string }
        | { id?: string; 'aria-label': string }
    );

type ComboboxMode = 'select' | 'freeText';

type ComboboxBaseProps = CommonProps & {
    mode: ComboboxMode;
    items: readonly ComboboxItem[];
    'aria-label'?: string;
    id?: string;
    invalid?: boolean;
    placeholder?: string;
    hint?: string;
};

interface ComboboxState {
    isOpen: boolean;
    /** Index into the visible items of the option Enter (and in select mode Tab) would pick; -1 for none. */
    highlightedIndex: number;
    /**
     * Text typed since the value was last picked, restored (select mode) or set from outside (free-text
     * mode); null while nothing is being filtered.
     * In select mode the input shows it in place of `value`; in free-text mode it is also the value.
     */
    filterText: string | null;
}

const noSuggestionsText = 'No suggestions';

const lowerCase = (text: string) => text.toLowerCase();

/**
 * An input that can be typed into to filter its list: the WAI-ARIA combobox pattern with list
 * autocomplete. Use it through {@link SelectCombobox} or {@link FreeTextCombobox}, which differ in what
 * the value can be. Picking an option sets the value from it (clicking it, or Enter with one
 * highlighted, or in select mode Tab); Escape and leaving the field any other way close the list without
 * picking.
 *
 * Hand-rolled rather than built on an autocomplete library such as downshift: those report changes from
 * effects and, for a select, treat the typed text as the value, which fights a value restricted to the
 * list. One component keeps one keyboard and highlight model for both modes.
 */
class ComboboxBase extends React.Component<ComboboxBaseProps, ComboboxState> {
    private readonly id = comboboxIdGen();
    private readonly inputRef = React.createRef<HTMLInputElement>();
    private readonly listboxRef = React.createRef<HTMLDivElement>();
    // Set whenever the highlight moves other than by hovering, so hovering an option never scrolls the list.
    private scrollToHighlight = false;
    // Set when Escape closed the list, so its keyup is kept from an enclosing modal too.
    private escapeClosedList = false;

    state: ComboboxState = { isOpen: false, highlightedIndex: -1, filterText: null };

    // A value changed from outside under a whole-text selection (another field's blur can land after focus
    // selected it) stays selected, so typing still replaces it. Picks are excluded by `!prevState.isOpen`, since
    // every pick is made from an open list.
    getSnapshotBeforeUpdate(prevProps: ComboboxBaseProps, prevState: ComboboxState) {
        const input = this.inputRef.current;
        return (
            prevProps.value !== this.props.value &&
            !prevState.isOpen &&
            prevState.filterText === null &&
            this.state.filterText === null &&
            input !== null &&
            input === document.activeElement &&
            input.selectionStart === 0 &&
            input.selectionEnd === prevProps.value.length
        );
    }

    componentDidUpdate(prevProps: ComboboxBaseProps, _prevState: ComboboxState, keepSelected: boolean) {
        if (keepSelected) {
            this.inputRef.current?.select();
        }
        // A free-text value set from outside (a form reset, another record opened for editing) is not typed text.
        const { filterText } = this.state;
        if (
            this.freeText &&
            prevProps.value !== this.props.value &&
            filterText !== null &&
            filterText !== this.props.value
        ) {
            this.setState({ filterText: null });
        }
        if (!this.scrollToHighlight) {
            return;
        }
        this.scrollToHighlight = false;
        this.listboxRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
    }

    private get freeText() {
        return this.props.mode === 'freeText';
    }

    private normalize(text: string) {
        return (this.props.normalize ?? lowerCase)(text);
    }

    private visibleFor(filterText: string | null) {
        if (!filterText?.trim()) {
            return this.props.items;
        }
        const text = this.normalize(filterText.trim());
        return this.props.items.filter(
            (item) => this.normalize(item.label).includes(text) || this.normalize(item.value).includes(text)
        );
    }

    /**
     * The current value when no text is typed; otherwise an exact match, else (in select mode only) the
     * first match. A free-text field never highlights a partial match, which Enter would put in place of
     * the typed text.
     */
    private highlightFor(filterText: string | null, visible: readonly ComboboxItem[]) {
        if (!filterText?.trim()) {
            return visible.findIndex((item) => item.value === this.props.value);
        }
        const text = this.normalize(filterText.trim());
        const exactIndex = visible.findIndex(
            (item) => this.normalize(item.label) === text || this.normalize(item.value) === text
        );
        return exactIndex >= 0 || this.freeText ? exactIndex : visible.length > 0 ? 0 : -1;
    }

    private open() {
        const { filterText } = this.state;
        // Focus alone misses reopening an already-focused input, so select here too: typing then starts a fresh filter.
        if (filterText === null) {
            this.inputRef.current?.select();
        }
        this.scrollToHighlight = true;
        this.setState({ isOpen: true, highlightedIndex: this.highlightFor(filterText, this.visibleFor(filterText)) });
    }

    private close() {
        this.setState({ isOpen: false, highlightedIndex: -1 });
    }

    private select(value: string) {
        this.setState({ isOpen: false, highlightedIndex: -1, filterText: null });
        if (value !== this.props.value) {
            this.props.onChange(value);
        }
    }

    // A free-text value is already the typed text, which stays as the filter for the search button.
    private restore() {
        if (this.freeText) {
            this.close();
        } else {
            this.select(this.props.value);
        }
    }

    private handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const filterText = e.currentTarget.value;
        // A free-text field is a text field first: typing only filters a list already open.
        const isOpen = !this.freeText || this.state.isOpen;
        this.scrollToHighlight = isOpen;
        this.setState({
            isOpen,
            filterText,
            highlightedIndex: isOpen ? this.highlightFor(filterText, this.visibleFor(filterText)) : -1,
        });
        if (this.freeText) {
            this.props.onChange(filterText);
        }
    };

    // Flushed: the focus can blur another field whose change to `value` must render before the list opens on it.
    private handleToggleClick = () => {
        flushSync(() => this.inputRef.current?.focus());
        this.toggle();
    };

    private toggle = () => {
        if (this.state.isOpen) {
            this.setState({ isOpen: false, highlightedIndex: -1 });
        } else {
            this.open();
        }
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.nativeEvent.isComposing) {
            return;
        }
        const { isOpen, highlightedIndex, filterText } = this.state;
        const rows = this.visibleFor(filterText);

        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                e.preventDefault();
                if (!isOpen) {
                    this.open();
                    return;
                }
                if (rows.length === 0) {
                    return;
                }
                const step = e.key === 'ArrowDown' ? 1 : -1;
                const from = highlightedIndex < 0 ? (step > 0 ? -1 : rows.length) : highlightedIndex;
                this.scrollToHighlight = true;
                this.setState({ highlightedIndex: Math.min(Math.max(from + step, 0), rows.length - 1) });
                return;
            }
            case 'Enter': {
                // On a closed list Enter belongs to an enclosing form; in select mode discard any typed text
                // first, so the form submits the value the field shows.
                if (!isOpen) {
                    this.restore();
                    return;
                }
                e.preventDefault();
                const row = rows[highlightedIndex];
                if (row) {
                    this.select(row.value);
                } else {
                    this.restore();
                }
                return;
            }
            case 'Escape':
                // An Escape that closes the list closes only the list: an enclosing modal may close on
                // keydown (ConfirmModal) or keyup (reactstrap's Modal).
                this.escapeClosedList = isOpen;
                if (isOpen) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                this.restore();
                return;
            // In select mode Tab is a keyboard user's "done": take the highlighted option, then let focus
            // move on; with nothing highlighted, the blur that follows restores the value. A free-text value
            // is already the typed text, so Tab keeps it rather than replacing it with a highlight that may
            // only be where the pointer passed.
            case 'Tab': {
                const row = isOpen && !this.freeText ? rows[highlightedIndex] : undefined;
                if (row !== undefined) {
                    this.select(row.value);
                }
                return;
            }
        }
    };

    private handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape' && this.escapeClosedList) {
            this.escapeClosedList = false;
            e.stopPropagation();
        }
    };

    private highlight(index: number) {
        if (index !== this.state.highlightedIndex) {
            this.setState({ highlightedIndex: index });
        }
    }

    render() {
        const {
            value,
            'aria-label': ariaLabel,
            className,
            emptyText,
            hint,
            items,
            id,
            invalid,
            placeholder,
        } = this.props;
        const { freeText } = this;
        const { isOpen, highlightedIndex, filterText } = this.state;
        const rows = this.visibleFor(filterText);
        // A free-text field with no suggestions at all (none yet, or none to offer) has nothing for the text to miss.
        const emptyMessage =
            isOpen && rows.length === 0 ? (freeText && items.length === 0 ? noSuggestionsText : emptyText) : '';
        const listboxId = `${this.id}-listbox`;
        const hintId = `${this.id}-hint`;
        const toggleId = `${this.id}-toggle`;
        const optionId = (index: number) => `${this.id}-option-${index}`;

        const input = (
            <input
                type="text"
                className={freeText ? `form-control${invalid ? ' is-invalid' : ''}` : 'form-select'}
                role="combobox"
                id={id}
                placeholder={placeholder}
                aria-label={ariaLabel}
                aria-invalid={invalid || undefined}
                aria-autocomplete={freeText && !isOpen ? 'none' : 'list'}
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={isOpen && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined}
                aria-describedby={hint ? hintId : undefined}
                autoComplete="off"
                spellCheck={false}
                value={freeText ? value : (filterText ?? value)}
                onChange={this.handleChange}
                ref={this.inputRef}
                // A free-text field is a text field first: clicking it places the caret rather than opening the list.
                onClick={freeText ? undefined : this.toggle}
                // The mouseup default can collapse the selection made by onFocus or open() (WebKit on
                // the focusing click, Chromium on a click into selected text).
                onMouseUp={(e) => {
                    if (!freeText && !isOpen && filterText === null) {
                        e.preventDefault();
                    }
                }}
                onKeyDown={this.handleKeyDown}
                onKeyUp={this.handleKeyUp}
                onFocus={freeText ? undefined : (e) => e.currentTarget.select()}
                onBlur={() => this.restore()}
            />
        );

        return (
            <div
                className={`combobox${freeText ? ' combobox-free-text' : ''}${invalid ? ' is-invalid' : ''}${className ? ` ${className}` : ''}`}
            >
                {freeText ? (
                    <div className="input-group">
                        {input}
                        <button
                            type="button"
                            id={toggleId}
                            className="btn btn-secondary"
                            aria-expanded={isOpen}
                            aria-controls={listboxId}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={this.handleToggleClick}
                        >
                            <i className="icon ion-md-search" aria-hidden="true"></i>
                            <span className="visually-hidden">Suggestions</span>
                        </button>
                    </div>
                ) : (
                    <>
                        {input}
                        {/* Covers the caret for a pointer cursor. Out of the tab order, as the input already handles the keyboard. */}
                        <button
                            type="button"
                            className="combobox-toggle"
                            tabIndex={-1}
                            aria-label={ariaLabel}
                            aria-expanded={isOpen}
                            aria-controls={listboxId}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={this.handleToggleClick}
                        />
                    </>
                )}
                {/* biome-ignore lint/a11y/noStaticElementInteractions: preventDefault only keeps focus in the input when the menu (e.g. its scrollbar) is pressed */}
                <div className={`dropdown-menu${isOpen ? ' show' : ''}`} onMouseDown={(e) => e.preventDefault()}>
                    {freeText ? (
                        <div className="dropdown-header">Suggestions</div>
                    ) : (
                        // Kept mounted (hidden while filtering) so the input's aria-describedby always resolves.
                        hint && (
                            <div id={hintId} className={filterText?.trim() ? 'd-none' : 'dropdown-header'}>
                                {hint}
                            </div>
                        )
                    )}
                    <div
                        ref={this.listboxRef}
                        id={listboxId}
                        role="listbox"
                        aria-label={freeText ? undefined : ariaLabel}
                        aria-labelledby={freeText ? toggleId : undefined}
                    >
                        {isOpen &&
                            rows.map((row, index) => (
                                // biome-ignore lint/a11y/useFocusableInteractive: focus stays in the input (aria-activedescendant)
                                // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection is handled by the input
                                <div
                                    key={row.value}
                                    id={optionId(index)}
                                    role="option"
                                    aria-selected={index === highlightedIndex}
                                    className={`dropdown-item${index === highlightedIndex ? ' active' : ''}`}
                                    onMouseMove={() => this.highlight(index)}
                                    onClick={() => this.select(row.value)}
                                >
                                    {row.label}
                                </div>
                            ))}
                    </div>
                    {emptyMessage && (
                        <div className="dropdown-item-text text-body-secondary" aria-hidden="true">
                            {emptyMessage}
                        </div>
                    )}
                </div>
                {/* Always mounted: a live region that appears together with its text is not reliably announced. */}
                <div role="status" className="visually-hidden">
                    {emptyMessage}
                </div>
            </div>
        );
    }
}

/** A select whose list can be filtered by typing. Only an item can be the value; typed text is never committed. */
export class SelectCombobox extends React.Component<SelectComboboxProps> {
    render() {
        const { items, ...props } = this.props;
        return <ComboboxBase {...props} mode="select" items={items.map((item) => ({ value: item, label: item }))} />;
    }
}

/**
 * A text field with suggestions: the value is whatever is typed, and picking a suggestion sets its
 * `value`. Typing only types: the search button or ArrowDown opens the list, filtered by any text typed.
 */
export class FreeTextCombobox extends React.Component<FreeTextComboboxProps> {
    render() {
        return <ComboboxBase {...this.props} mode="freeText" />;
    }
}
